import React, { useRef, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';

import Icon from '../icon';
import IconButton from '../icon-button';
import noop from '../../../utils/noop';
import { SIZES } from './constants';

const Search = ({
  defaultValue = '',
  disabled = false,
  id = undefined,
  invalid = false,
  onChange,
  onClear,
  onFocus = noop,
  placeholder = 'Search...',
  size = SIZES.small,
  disableClearWhileInput = false,
  transparent = false,
}) => {
  const [areActionButtonsVisible, setActionButtonsVisibility] = useState(false);
  const inputRef = useRef(null);
  const isMobile = bowser.mobile;

  const labelClasses = classNames('ws-ui-search', `ws-ui-search--${size}`, {
    'ws-ui-search--transparent': transparent,
    'ws-ui-search--disabled': disabled,
    'ws-ui-search--invalid': invalid,
    'ws-ui-search--mobile': isMobile,
  });

  const inputClasses = classNames('ws-ui-search__input', {
    'ws-ui-search__input--invalid': invalid,
    'ws-ui-search__input--transparent': transparent,
  });

  const handleFocus = () => {
    const { value } = inputRef.current;

    onFocus(value);
  };

  const handleClear = useCallback(
    (e) => {
      if (e) e.preventDefault();

      inputRef.current.value = '';

      setActionButtonsVisibility(false);

      if (onClear) onClear();

      // eslint-disable-next-line yola/react-hooks/exhaustive-deps
    },
    [onClear]
  );

  const handleInput = (event) => {
    const { value } = event.target;

    if (!disableClearWhileInput && !value) handleClear();

    setActionButtonsVisibility(!!value.length);
  };

  const handleSearch = useCallback((value) => onChange(value), [onChange]);

  const handleKeyDown = (event) => {
    const {
      key,
      target: { value },
    } = event;

    if (key === 'Enter') handleSearch(value);
  };

  const handleSearchClick = () => {
    const { value } = inputRef.current;

    handleSearch(value);
  };

  useEffect(() => {
    setActionButtonsVisibility(!!defaultValue.length);
  }, [defaultValue]);

  return (
    <label className={labelClasses} htmlFor={id}>
      <span className="ws-ui-search__icon-wrap ws-ui-search__icon-wrap--main">
        <Icon glyph="search" type="inherit" />
      </span>
      <input
        className={inputClasses}
        defaultValue={defaultValue}
        disabled={disabled}
        inputMode="search"
        key={defaultValue}
        onFocus={handleFocus}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        ref={inputRef}
        type="text"
        id={id}
      />
      {!disabled && areActionButtonsVisible && (
        <span className="ws-ui-search__icon-wrap ws-ui-search__icon-wrap--actions">
          <IconButton onClick={handleClear} glyph="clear-search" size="medium" />
          <IconButton onClick={handleSearchClick} glyph="enter" size="medium" />
        </span>
      )}
    </label>
  );
};

Search.propTypes = {
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  invalid: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(Object.keys(SIZES)),
  disableClearWhileInput: PropTypes.bool,
  transparent: PropTypes.bool,
};

export default Search;
